import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { User, Company, AuthState, LoginCredentials, RegisterData } from '../types';
import { apiHelpers } from '../utils/api';

interface AuthStore extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<void>;
  updateUser: (user: Partial<User>) => void;
  updateCompany: (company: Partial<Company>) => void;
  setLoading: (loading: boolean) => void;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      company: null,
      token: null,
      isAuthenticated: false,
      loading: false,

      login: async (credentials: LoginCredentials) => {
        try {
          console.log('🔥 AUTH STORE: Login attempt started');
          console.log('🔥 AUTH STORE: Credentials:', credentials);
          set({ loading: true });

          console.log('🔥 AUTH STORE: Calling apiHelpers.post to /auth/login');
          const response = await apiHelpers.post<{
            success: boolean;
            message: string;
            data: {
              user: User;
              company: Company;
              token: string;
              refreshToken: string;
            };
          }>('/auth/login', credentials);

          console.log('🔥 AUTH STORE: Login response received:', response);
          const { user, company, token, refreshToken } = response.data;

          // Store tokens
          localStorage.setItem('token', token);
          localStorage.setItem('refreshToken', refreshToken);

          set({
            user,
            company,
            token,
            isAuthenticated: true,
            loading: false,
          });
          console.log('✅ AUTH STORE: Login successful, state updated');
        } catch (error) {
          console.error('❌ AUTH STORE: Login failed:', error);
          console.error('❌ AUTH STORE: Error details:', error);
          set({ loading: false });
          throw error;
        }
      },

      register: async (data: RegisterData) => {
        try {
          set({ loading: true });
          
          const response = await apiHelpers.post<{
            success: boolean;
            message: string;
            data: {
              user: User;
              company: Company;
              token: string;
              refreshToken: string;
            };
          }>('/auth/register', data);

          const { user, company, token, refreshToken } = response.data;

          // Store tokens
          localStorage.setItem('token', token);
          localStorage.setItem('refreshToken', refreshToken);

          set({
            user,
            company,
            token,
            isAuthenticated: true,
            loading: false,
          });
        } catch (error) {
          set({ loading: false });
          throw error;
        }
      },

      logout: () => {
        // Clear tokens
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');

        set({
          user: null,
          company: null,
          token: null,
          isAuthenticated: false,
          loading: false,
        });
      },

      refreshToken: async () => {
        try {
          const refreshToken = localStorage.getItem('refreshToken');
          if (!refreshToken) {
            throw new Error('No refresh token available');
          }

          const response = await apiHelpers.post<{
            success: boolean;
            message: string;
            data: {
              token: string;
              refreshToken: string;
            };
          }>('/auth/refresh', { refreshToken });

          const { token, refreshToken: newRefreshToken } = response.data;

          // Update tokens
          localStorage.setItem('token', token);
          localStorage.setItem('refreshToken', newRefreshToken);

          set({ token });
        } catch (error) {
          // If refresh fails, logout user
          get().logout();
          throw error;
        }
      },

      updateUser: (userData: Partial<User>) => {
        const currentUser = get().user;
        if (currentUser) {
          set({ user: { ...currentUser, ...userData } });
        }
      },

      updateCompany: (companyData: Partial<Company>) => {
        const currentCompany = get().company;
        if (currentCompany) {
          set({ company: { ...currentCompany, ...companyData } });
        }
      },

      setLoading: (loading: boolean) => {
        set({ loading });
      },
    }),
    {
      name: 'auth-store',
      partialize: (state) => ({
        user: state.user,
        company: state.company,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: (_state) => {
        return (state, error) => {
          if (error) {
            console.error('Auth store rehydration error:', error);
          }
        };
      },
    }
  )
);